import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-car-tunnel',
    title: '1. Software-Defined Vehicle (SDV) Platforms',
    desc: 'Engineer decoupled, modular software architectures that enable Over-The-Air (OTA) updates, cloud-native vehicle features, and flexible ECU configurations.',
  },
  {
    icon: 'fa-solid fa-satellite-dish',
    title: '2. Connected Vehicle & Telematics Clouds',
    desc: 'Build high-throughput telemetry pipelines using MQTT/HTTP to ingest real-time vehicle diagnostic logs, location data, and driver behavioral metrics.',
  },
  {
    icon: 'fa-solid fa-microchip',
    title: '3. AUTOSAR Embedded Systems & Middleware',
    desc: 'Develop Classic and Adaptive AUTOSAR compliant ECU firmware, ensuring high safety-critical determinism, bus communications (CAN/LIN/Ethernet), and security.',
  },
  {
    icon: 'fa-solid fa-car-burst',
    title: '4. ADAS & Autonomous Driving Data Pipelines',
    desc: 'Construct petabyte-scale data lakes and AI model training pipelines for camera, LiDAR, and radar sensor fusion to accelerate ADAS development.',
  },
  {
    icon: 'fa-solid fa-charging-station',
    title: '5. Electric Vehicle (EV) & Battery Management (BMS)',
    desc: 'Optimize EV powertrain performance, monitor state-of-charge (SoC) degradation models, and streamline smart charging grid communication protocols (ISO 15118).',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '6. Automotive Cybersecurity (ISO/SAE 21434)',
    desc: 'Implement threat analysis, secure bootloader mechanisms, Intrusion Detection and Prevention Systems (IDPS), and regulatory compliance for connected fleets.',
  },
  {
    icon: 'fa-solid fa-fleet',
    title: '7. Smart Fleet Management & Predictive Maintenance',
    desc: 'Deploy fleet telematics solutions featuring predictive component wear analysis, route optimization algorithms, and automated fuel efficiency tracking.',
  },
  {
    icon: 'fa-solid fa-mobile-screen-button',
    title: '8. In-Vehicle Infotainment (IVI) & Smart Cockpits',
    desc: 'Design intuitive digital cockpits powered by Android Automotive OS and Automotive Grade Linux, featuring voice control and seamless smartphone integration.',
  },
  {
    icon: 'fa-solid fa-vr-cardboard',
    title: '9. Digital Twin for Automotive Manufacturing',
    desc: 'Simulate vehicle aerodynamics, stress testing, and assembly line logistics using virtual 3D rendering engines and physics-based Digital Twins.',
  },
  {
    icon: 'fa-solid fa-store',
    title: '10. Dealership & OEM Mobility Integrations',
    desc: 'Connect connected vehicle telemetry directly with OEM dealership networks for automated service booking, recall alerts, and digital sales portals.',
  },
];

const whyChooseUs = [
  'Deep expertise in ISO 26262 (Functional Safety) and ISO/SAE 21434 (Cybersecurity)',
  'Adaptive and Classic AUTOSAR software stack development capability',
  'Scalable cloud infrastructure supporting millions of concurrent connected vehicles',
  'Over-The-Air (OTA) update frameworks with zero-risk rollback mechanisms',
  'End-to-end ADAS dataset annotation, simulation, and ML training pipelines',
  'Android Automotive OS (AAOS) and Automotive Grade Linux customization',
  'EV battery optimization and smart charging station integration (OCPP/ISO 15118)',
  'Real-time streaming pipelines for vehicle telematics and remote diagnostics',
  'Seamless integration with OEM supply chain, MES, and ERP environments',
  '24/7 vehicle Security Operations Center (vSOC) and cloud infrastructure support',
];

const processSteps = [
  { step: '01', title: 'Vehicle Architecture & Safety Audit', desc: 'Assess ECU network topology, bus protocols, cloud connectivity, and functional safety (ISO 26262) compliance.' },
  { step: '02', title: 'Embedded & Cloud Strategy', desc: 'Design AUTOSAR compliant firmware stacks alongside high-throughput cloud telematics ingestion pipelines.' },
  { step: '03', title: 'Software & OTA Development', desc: 'Develop embedded middleware, digital cockpit UIs, and cloud OTA deployment orchestration mechanisms.' },
  { step: '04', title: 'Cybersecurity & HIL Stress Testing', desc: 'Execute Hardware-in-the-Loop (HIL) simulations, penetration testing, and vSOC threat monitoring integration.' },
  { step: '05', title: 'Fleet Deployment & Integration', desc: 'Deploy cloud pipelines, roll out initial OTA firmware updates, and integrate with OEM dealership systems.' },
  { step: '06', title: 'Continuous Telematics & AI Tuning', desc: 'Ingest real-world fleet telemetry data, monitor battery health models, and continuously refine ADAS algorithms.' },
];

const techStack = [
  { category: 'Embedded & OS', items: ['AUTOSAR', 'QNX', 'Android Automotive OS', 'Automotive Grade Linux', 'FreeRTOS'] },
  { category: 'Protocols & Connectivity', items: ['CAN / CAN-FD', 'SOME/IP', 'Automotive Ethernet', 'MQTT', 'ISO 15118'] },
  { category: 'Cloud & AI Platforms', items: ['AWS IoT FleetWise', 'Azure Connected Vehicle', 'PyTorch', 'ROS2', 'OpenCV'] },
  { category: 'Safety & Simulation', items: ['ISO 26262 (ASIL)', 'ISO/SAE 21434', 'CARLA', 'MATLAB/Simulink', 'dSPACE'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const AutomotiveSolutions = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Automotive Engineering
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Automotive <span className="gradient-text-cyan">Solutions</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we build the software platforms driving the future of mobility. From Software-Defined Vehicles (SDVs) and connected telematics clouds to ADAS data pipelines and AUTOSAR compliance, we power next-generation automotive ecosystems.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Drive Your Automotive Strategy &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Automotive Engineering Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Automotive Technology Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Automotive Engineering Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Shape the Future of Mobility with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform your vehicle fleet into intelligent, software-defined assets with cloud connectivity, advanced safety compliance, and OTA capabilities.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with an Automotive Specialist &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AutomotiveSolutions;